import getSdkSession from '../utils/getSdkSession';

const Bam = {

    /**
     *
     * @returns {Object} - a reference to the SDK session singleton
     *
     */

    getSdkSession,

    /**
     *
     * @access public
     * @desc Determines whether the user has successfully logged in with the BAMTECH identity service.
     * Does not check localStorage.
     * @returns {Promise<boolean>} Resolves true if logged in, false if logged out.
     *
     */
    async isLoggedIn() {
        const sdkSession = await Bam.getSdkSession();
        const loggedInState = await sdkSession.isAuthorized('bamIdentity');
        return loggedInState;
    },

    /**
     *
     * @access public
     * @param {String} email - The email entered by the user.
     * @desc Check against user service to determine if the email exists in our system.
     * @note Users who have unverified identities with OneID may be sent through One-Time Passcode flow.
     * @returns {Promise<Array>} Resolves with an array of flows.
     *
     */
    async getAuthenticationFlow(email) {
        if (!email || typeof email !== 'string') {
            throw new Error('You must pass email string to getAuthenticationFlow function.');
        }
        const sdkSession = await Bam.getSdkSession();
        const { bamIdentityApi } = sdkSession;
        const flows = await bamIdentityApi.getAuthenticationFlow(email);
        return flows;
    },

    /**
     *
     * @access public
     * @param {String} email - The email associated with the identity.
     * @param {String} password - The password associated with the identity.
     * @desc exchanges an email and password for an identity token.
     * @returns {Promise} Resolves with an identity token.
     *
     */
    async authenticate(email, password) {
        if (
            !email ||
            !password ||
            typeof email !== 'string' ||
            typeof password !== 'string'
        ) {
            throw new Error('You must pass email and password strings to authenticate function.');
        }
        const sdkSession = await Bam.getSdkSession();
        const { bamIdentityApi } = sdkSession;
        return bamIdentityApi.authenticate(email, password);
    },

    /**
     *
     * @access public
     * @param {String} identityToken - an identity token to be exchanged for an accountGrant
     * @desc Exchanges an identity token for an account grant.
     * @returns {Promise} Resolves with an access token.
     *
     */
    async authorize(identityToken) {
        if (!identityToken || typeof identityToken !== 'string') {
            throw new Error('You must pass an identityToken string to authorize.');
        }
        const sdkSession = await Bam.getSdkSession();
        const { accountApi } = sdkSession;
        try {
            const result = await accountApi.authorize(identityToken, 'bamIdentity');
            return result;
        } catch (error) {
            if (
                error &&
                error.reasons &&
                error.reasons[0] &&
                error.reasons[0].code === 'account.not.found'
            ) {
                await accountApi.createAccount(identityToken);
                return await accountApi.authorize(identityToken, 'bamIdentity');
            } else {
                throw error;
            }
        }
    },

    /**
     *
     * @access public
     * @param {String} email - The email associated with the identity.
     * @param {String} password - The password associated with the identity.
     * @desc Logs the user in with the BAMTECH identity service.
     * @note Creates Bam identity token, exchanges for an account grant.
     * @returns {Promise} Resolves with the called SDK authentiate function.
     *
     */
    async login(email, password) {
        if (
            !email ||
            !password ||
            typeof email !== 'string' ||
            typeof password !== 'string'
        ) {
            throw new Error('You must pass email and password strings to login function.');
        }
        const identityTokenObject = await Bam.authenticate(email, password);
        const { token } = identityTokenObject;
        return Bam.authorize(token);
    },

    /**
     *
     * @access public
     * @desc Logs the user out by resetting the access token to an anonymous state.
     * @returns {Promise} Resolves with the called SDK reset function.
     *
     */
    async logout() {
        const sdkSession = await Bam.getSdkSession();
        return sdkSession.reset();
    },

    /**
     *
     * @access public
     * @param {String} email - The email to create a new identity for.
     * @param {String} password - The password to create for the new identity.
     * @desc Creates a Bam Identity with the credentials provided.
     * @note Registers a new Bam identity, obtains an identity token, creates a new account,
     * exchanges for an account grant, and exchanges account grant for a new AccessContext.
     * @returns {Promise} Resolves with the called SDK createIdentity function.
     *
     */
    async createIdentity(email, password, attributes) {
        if (!email || !password || !attributes) {
            throw new Error('You must pass email, password, and an attributes object to createIdentity function.');
        }
        const sdkSession = await Bam.getSdkSession();
        const { bamIdentityApi } = sdkSession;
        const identityTokenObject = await bamIdentityApi.createIdentity({
            attributes,
            email,
            password
        });
        const { token } = identityTokenObject;
        return Bam.authorize(token);
    },

    /**
     *
     * @access public
     * @param {String} email - The email associated with the identity that needs to reset the password.
     * @desc Given an email for the user, requests a password reset email.
     * @note Requests a password reset with the bam identity service. This is part of the email clickback flow.
     * The rest of the flow is completed by the user after receiving the email.
     * @returns {Promise} Resolves with the called SDK requestPasswordReset function.
     *
     */
    async requestPasswordReset(email) {
        if (!email) {
            throw new Error('You must pass email to requestPasswordReset function.');
        }
        const sdkSession = await Bam.getSdkSession();
        return sdkSession.bamIdentityApi.requestPasswordReset(email);
    },

    /**
     *
     * @access public
     * @param {String} newPassword - The new password to associate with the existing identity.
     * @param {String} resetToken - The reset token provided via email to verify the owner of the account.
     * @desc Following a requestPasswordReset, use this method to update the
     * user's password with the token provided in the password reset email.
     * @note Requests a password update using the token supplied and the new password.
     * @returns {Promise} Resolves with the called SDK resetPassword function.
     *
     */
    async resetPassword(newPassword, resetToken) {
        if (!newPassword || !resetToken) {
            throw new Error('You must pass newPassword and resetToken to resetPassword function.');
        }
        const sdkSession = await Bam.getSdkSession();
        return sdkSession.bamIdentityApi.resetPassword(newPassword, resetToken);
    },

    /**
     *
     * @access public
     * @param {String} email - User's email
     * @param {String} reason - Either "Login" or "ForgotPassword"
     * @desc Request one time passcode to the specified email
     * @note Step one of the one time passcode flow.
     * @returns {Promise} Resolves with the called SDK requestOneTimePasscode function.
     *
     */
    async requestOneTimePasscode(email, reason) {
        if (
            !email ||
            !reason ||
            typeof email !== 'string' ||
            typeof reason !== 'string'
        ) {
            throw new Error('You must pass email string and reason string to requestOneTimePasscode function.');
        }

        const sdkSession = await Bam.getSdkSession();
        const { bamIdentityApi } = sdkSession;

        return bamIdentityApi.requestOneTimePasscode(email, reason);
    },

    /**
     *
     * @access public
     * @param {String} email - User's email
     * @param {String} passcode - One time passcode is a six digit string
     * @desc Redeems a one time passcode for an identity token
     * @note Step two of the one time passcode flow.
     * @returns {Promise} Resolves with the identity token.
     *
     */
    async authenticateWithPasscode(email, passcode) {
        if (
            !email ||
            !passcode ||
            typeof email !== 'string' ||
            typeof passcode !== 'string'
        ) {
            throw new Error('You must pass email string and passcode string to authenticateWithPasscode function.');
        }

        const sdkSession = await Bam.getSdkSession();
        const { bamIdentityApi } = sdkSession;

        return bamIdentityApi.authenticateWithPasscode(email, passcode);
    },

    /**
     *
     * @access public
     * @desc Indicates if authentication is needed
     * @note Certain actions require that the user authenticated recently
     * This method will return true if the user is requried to re-authenticate
     * @returns {Promise} Resolves with called SDK requiresAuthentication function.
     *
     */
    async requiresAuthentication() {
        const sdkSession = await Bam.getSdkSession();
        const { bamIdentityApi } = sdkSession;

        return bamIdentityApi.requiresAuthentication();
    },

    /**
     *
     * @access public
     * @param {String} newEmail - The new email to change the identity to use.
     * @param {String} password - The current password for verification purposes.
     * @desc Updates the user's email for the BAMTECH Identity.
     * The current email & password must be provided for verification.
     * @note Gets an identity token using the login credentials, issues request to
     * change email using that identity token and the new email.
     * @returns {Promise} Resolves with called SDK updateEmail function.
     *
     */
    async updateEmail(newEmail, password) {
        if (!newEmail || !password) {
            throw new Error('You must pass newEmail and password strings to updateEmail function.');
        }
        const sdkSession = await Bam.getSdkSession();
        return sdkSession.bamIdentityApi.updateEmail(newEmail, password);
    },

    /**
     *
     * @access public
     * @param {String} newPassword - The new password to associate with the current identity.
     * @param {String} [oldPassword] - The current password for verification purposes.
     * @desc Updates the user's password for the BAMTECH Identity.
     * The current email & password must be provided for verification.
     * @note Gets an identity token using the login credentials, issues request to
     * change password using that identity token and the new password.
     * @returns {Promise} Resolves with called SDK updatePassword function.
     *
     */
    async updatePassword(newPassword, oldPassword) {
        if (
            !newPassword ||
            typeof newPassword !== 'string'
        ) {
            throw new Error('You must pass a newPassword string to updatePassword function.');
        }
        if (oldPassword && typeof oldPassword !== 'string') {
            throw new Error('oldPassword must be of type string.');
        }
        const sdkSession = await Bam.getSdkSession();
        return sdkSession.bamIdentityApi.updatePassword(newPassword, oldPassword);
    },

    async getAttribute(key) {
        if (typeof key !== 'string') {
            throw new Error('You must pass an attribute string to getAttribute.');
        }
        const attributes = await Bam.getAttributes();
        const attribute = attributes[key];
        if (attribute === undefined) {
            console.error(`${key} attribute not found on bamIdentityApi.`);
        }
        return attribute;
    },

    async getAttributes() {
        const sdkSession = await Bam.getSdkSession();
        const { bamIdentityApi } = sdkSession;
        return bamIdentityApi.attributes;
    },

    async setAttribute(key, newValue) {
        if ((typeof key !== 'string') || (newValue === undefined)) {
            throw new Error('You must pass an attribute string and a new value to setAttribute.');
        }
        const newAttribute = {};
        newAttribute[key] = newValue;
        return Bam.setAttributes(newAttribute);
    },

    async setAttributes(newAttributes) {
        if (!newAttributes || (typeof newAttributes !== 'object')) {
            throw new Error('You must pass an object to setAttributes that contains the key/value pairs of attributes you want to update.');
        }
        const sdkSession = await Bam.getSdkSession();
        const { bamIdentityApi } = sdkSession;
        const identity = await bamIdentityApi.getIdentity();
        identity.attributes = newAttributes;
        return bamIdentityApi.updateIdentity(identity);
    }

};

export default Bam;
